import {REALTIME_DB} from '@/config/firebase';
import store from '@/redux/store';
import {IReference, ENoteCategory} from '@/types/airport';
import {ref, set, update} from 'firebase/database';

/**
 * Updates references for a specific airport category
 * @param icaoCode - Airport ICAO code
 * @param category - Airport category (arrival, departure, ground_operations, additional_information)
 * @param references - Array of references to update
 * @returns Promise<void>
 */
export const updateAirportReferences = async (
  icaoCode: string,
  category: ENoteCategory,
  references: IReference[]
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const categoryKey =
      category === ENoteCategory.GROUND_OPERATIONS
        ? 'ground_operations'
        : category;
    const referencesRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${icaoCode}/categories/${categoryKey}/references`
    );

    await set(referencesRef, references);
    console.log('References updated successfully for category:', category);
  } catch (error) {
    console.error('Error updating references:', error);
    throw error;
  }
};

/**
 * Updates main airport references (not category-specific)
 * @param icaoCode - Airport ICAO code
 * @param references - Array of main references to update
 * @returns Promise<void>
 */
export const updateAirportMainReferences = async (
  icaoCode: string,
  references: IReference[]
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const referencesRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${icaoCode}/references`
    );

    await set(referencesRef, references);
    console.log('Main airport references updated successfully');
  } catch (error) {
    console.error('Error updating main airport references:', error);
    throw error;
  }
};

/**
 * Updates fleet data for a specific airport category
 * @param icaoCode - Airport ICAO code
 * @param category - Airport category (arrival, departure, ground_operations, additional_information)
 * @param fleetType - Fleet type name
 * @param references - Array of references for this fleet type
 * @returns Promise<void>
 */
export const updateAirportFleet = async (
  icaoCode: string,
  category: ENoteCategory,
  fleetType: string,
  references: IReference[]
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const fleetRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${icaoCode}/categories/${category}/fleet/${fleetType}`
    );

    await set(fleetRef, references);
    console.log(
      'Fleet updated successfully for category:',
      category,
      'fleet type:',
      fleetType
    );
  } catch (error) {
    console.error('Error updating fleet:', error);
    throw error;
  }
};

export const updateStationInfo = async (
  icaoCode: string,
  stationData: Record<string, any>
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const stationRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${icaoCode.toUpperCase()}`
    );
    await update(stationRef, stationData);
  } catch (error) {
    console.error('Error updating airport station:', error);
    throw error;
  }
};

/**
 * Adds a new fleet type to a specific airport category with empty references
 * @param icaoCode - Airport ICAO code
 * @param category - Airport category (arrival, departure)
 * @param fleetType - Fleet type name
 * @returns Promise<void>
 */
export const addAirportFleetType = async (
  icaoCode: string,
  category: ENoteCategory,
  fleetType: string
): Promise<void> => {
  try {
    const airline = store.getState().user.airline;
    const fleetRef = ref(
      REALTIME_DB,
      `/airport_notes/${airline}/stations/${icaoCode}/categories/${category}/fleet/${fleetType}`
    );

    // Initialize with empty array
    await set(fleetRef, [
      {
        header: '',
        content: ''
      }
    ]);
    console.log(
      'Fleet type added successfully for category:',
      category,
      'fleet type:',
      fleetType
    );
  } catch (error) {
    console.error('Error adding fleet type:', error);
    throw error;
  }
};
